import React, { useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { useSelector } from "react-redux";
import Loading from "../../../Components/Loading";
import { Toast } from "primereact/toast";
import { useTranslation, Trans } from "react-i18next";

const getUploadErrorMessage = (uploadError, t) => {
    if (!uploadError) {
        return t("business_customers.upload_error");
    }

    const fieldErrors = uploadError?.fieldErrors ?? uploadError?.errors;
    if (Array.isArray(fieldErrors) && fieldErrors.length) {
        const messages = fieldErrors
            .map((item) => item?.message || item?.detail || item?.defaultMessage)
            .filter(Boolean);
        if (messages.length) {
            return messages.join(" ");
        }
    }

    return uploadError?.detail || uploadError?.message || uploadError?.title || t("business_customers.upload_error");
};

const EnrichConfirmation = ({ validFilteredRows, state, setState, navigateToEnrichList, handleBack }) => {
    const { t } = useTranslation("common");
    const toast = useRef(null);

    const uploadingCustomerEnrichment = useSelector((s) => s.businessCustomers.uploadingCustomerEnrichment);
    const enrichmentUpload = useSelector((s) => s.businessCustomers.enrichmentUpload);
    const enrichmentUploadError = useSelector((s) => s.businessCustomers.enrichmentUploadError);

    const validImports = validFilteredRows.length;
    const uploadResult = state.uploadedEnrichment || enrichmentUpload;

    useEffect(() => {
        if (uploadingCustomerEnrichment) {
            setState((prev) => ({ ...prev, uploading: true }));
        } else {
            setState((prev) => ({ ...prev, uploading: false }));
        }
        if (enrichmentUpload) {
            setState((prev) => ({ ...prev, uploadedEnrichment: enrichmentUpload }));
        }
    }, [uploadingCustomerEnrichment, enrichmentUpload, setState]);

    useEffect(() => {
        if (!uploadingCustomerEnrichment && enrichmentUploadError) {
            toast.current?.show({
                severity: "error",
                summary: t("business_customers.error_summary"),
                detail: getUploadErrorMessage(enrichmentUploadError, t),
                life: 5000,
            });
        }
    }, [uploadingCustomerEnrichment, enrichmentUploadError, t]);

    const uploadFailed = !uploadingCustomerEnrichment && !uploadResult && enrichmentUploadError;
    const waitingForUpload = uploadingCustomerEnrichment || (state.uploading && !uploadResult && !enrichmentUploadError);

    return (
        <div>
            <Toast ref={toast} />
            {waitingForUpload ? (
                <Loading />
            ) : uploadFailed ? (
                <Card className="main-upload-card">
                    <div className="pl-4 pr-4 pt-5">
                        <p className="title-text mb-0">{t("business_customers.error_summary")}</p>
                        <p className="sub_text mb-4 reveknew_red">
                            {getUploadErrorMessage(enrichmentUploadError, t)}
                        </p>
                    </div>
                    <div className="cont-btn">
                        <Button className="import_cancel_button" onClick={navigateToEnrichList}>
                            {t("cancel")}
                        </Button>
                        <Button className="import_button steps-button py-2" onClick={handleBack}>
                            {t("Back")}
                        </Button>
                    </div>
                </Card>
            ) : (
                uploadResult && (
                    <Card className="main-upload-card">
                        <div className="pl-4 pr-4 pt-5">
                            <p className="title-text mb-0">{t("business_customers.enrich_import_completed_header")}</p>
                            <p className="sub_text mb-0">{t("business_customers.enrich_import_completed_text")}</p>

                            <div className="enrich-success-box">
                                <p className="enrich-success-box-title">
                                    {t("business_customers.enrich_import_success_tip1")}
                                </p>
                                <p className="enrich-success-box-text">
                                    {t("business_customers.enrich_import_success_tip2")}
                                </p>
                                <p className="enrich-success-box-detail">
                                    <Trans
                                        i18nKey={
                                            validImports > 1
                                                ? "business_customers.enrich_import_success_tip3_plural"
                                                : "business_customers.enrich_import_success_tip3_single"
                                        }
                                        values={{
                                            count: uploadResult.entryCount ?? validImports,
                                            requestId: uploadResult.requestId,
                                        }}
                                        components={{
                                            1: <span className="success-red" />,
                                            2: <span className="success-green" />,
                                        }}
                                    />
                                </p>
                            </div>
                        </div>
                        <div className="cont-btn">
                            <Button className="import_done" onClick={navigateToEnrichList}>
                                {t("done")}
                            </Button>
                            <Button className="import_button steps-button py-2" onClick={handleBack}>
                                {t("business_customers.enrich_upload_another")}
                            </Button>
                        </div>
                    </Card>
                )
            )}
        </div>
    );
};

export default EnrichConfirmation;
